import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../assets/fullLogo2.PNG";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const badgePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(168, 85, 253, 0.4); }
  50%       { box-shadow: 0 0 0 4px rgba(168, 85, 253, 0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(100%); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(100%); }
`;

const overlayFade = keyframes`
  from { opacity: 0; visibility: hidden; }
  to   { opacity: 1; visibility: visible; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(1rem, 4vw, 3.5rem);
  height: 68px;
  animation: ${fadeDown} 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;

  background: rgba(10, 0, 16, 0.75);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border-bottom: 1px solid rgba(168, 85, 253, 0.15);

  transition:
    background 0.35s ease,
    backdrop-filter 0.35s ease,
    border-color 0.35s ease;

  &.scrolled {
    background: rgba(10, 0, 16, 0.75);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    border-color: rgba(168, 85, 253, 0.15);
  }
`;

// ── Logo
const LogoWrap = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;

  img {
    height: 32px;
    width: auto;
    object-fit: contain;
    display: block;
  }
`;

// ── Desktop Menu
const MenuList = styled.ul`
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 3vw, 2.8rem);
  list-style: none;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 1000px) {
    display: none;
  }
`;

const MenuItem = styled.li``;

const MenuLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: -3px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, #a855f7, #e879f9);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.25s cubic-bezier(0.23, 1, 0.32, 1);
    border-radius: 1px;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.95);
    &::after {
      transform: scaleX(1);
    }
  }
`;

// ── Mobile Menu Button (Hamburger)
const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  z-index: 1002;
  position: relative;

  @media (max-width: 1000px) {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: white;
    border-radius: 2px;
    transition: all 0.3s ease;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    span:nth-child(1) {
      transform: rotate(45deg) translate(5.5px, 5.5px);
    }
    span:nth-child(2) {
      opacity: 0;
    }
    span:nth-child(3) {
      transform: rotate(-45deg) translate(5.5px, -5.5px);
    }
  `}
`;

// ── Mobile Menu Overlay
const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
  animation: ${overlayFade} 0.3s ease forwards;

  @media (max-width: 1000px) {
    display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  }
`;

// ── Mobile Menu
const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  max-width: 320px;
  background: rgba(10, 0, 16, 0.98);
  backdrop-filter: blur(20px);
  z-index: 1001;
  padding: 80px 24px 32px;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.3);
  animation: ${({ $isOpen }) => ($isOpen ? slideIn : slideOut)} 0.3s ease
    forwards;

  @media (max-width: 1000px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileMenuList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const MobileMenuItem = styled.li``;

const MobileMenuLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: color 0.2s ease;
  display: block;
  padding: 8px 0;

  &:hover {
    color: #a855f7;
  }
`;

const MobileCtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
`;

const MobileTrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgb(255, 255, 255);
    white-space: nowrap;
  }
`;

// ── CTA wrapper (desktop)
const CtaWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 1000px) {
    gap: 12px;
  }
  @media (max-width: 600px) {
    /* display: none; */
  }
`;

// ── Official App Store button
const AppStoreBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  height: 44px;
  border-radius: 10px;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.18);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.97);
  }
  @media (max-width: 550px) {
    display: none;
  }
`;

const AppleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: -2px;

  svg {
    width: 18px;
    height: 18px;
    fill: #fff;
  }
`;

const AppStoreCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const AppStoreTop = styled.span`
  font-family: -apple-system, "SF Pro Text", "Helvetica Neue", sans-serif;
  font-size: 0.58rem;
  font-weight: 400;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
`;

const AppStoreBottom = styled.span`
  font-family: -apple-system, "SF Pro Display", "Helvetica Neue", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
  line-height: 1.1;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

// ── Desktop Trial Badge
const TrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);

  @media (max-width: 1000px) {
    display: none;
  }

  span {
    font-family: "DM Sans", sans-serif;
    font-size: 0.6rem;
    font-weight: 400;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgb(255, 255, 255);
    white-space: nowrap;
  }
`;

// ─── Nav items ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Help & Resources", href: "/help" },
  { label: "Contact", href: "/contact" },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <Nav className={scrolled ? "scrolled" : ""}>
        {/* Logo */}
        <LogoWrap href="/">
          <img src={logo} alt="Logo" />
        </LogoWrap>

        {/* Desktop Menu */}
        <MenuList>
          {NAV_LINKS.map(({ label, href }) => (
            <MenuItem key={href}>
              <MenuLink href={href}>{label}</MenuLink>
            </MenuItem>
          ))}
        </MenuList>

        {/* CTA Section */}
        <CtaWrap>
          <AppStoreBtn
            href="/waitlist"
            // target="_blank"
            rel="noopener noreferrer"
          >
            <AppleIcon>
              <svg viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 268.9-317.3 70.2 0 128.6 46.4 173 46.4 42.4 0 109.1-49.1 186.3-49.1 30.4 0 110.2 3.9 162.7 69.2zm-85.5-220.5c39.5-46.4 67.8-110.9 67.8-175.4 0-8.9-.6-17.8-2.2-25.4-64.4 2.6-140.5 43.7-185.5 93.8-35.8 39.5-69.7 103.4-69.7 168.5 0 9.5 1.6 19 2.2 22.3 3.9.6 10.4 1.6 16.9 1.6 57.7 0 129.4-39.5 170.5-85.4z" />
              </svg>
            </AppleIcon>
            <AppStoreCopy>
              <AppStoreTop>Download on the</AppStoreTop>
              <AppStoreBottom>App Store</AppStoreBottom>
            </AppStoreCopy>
          </AppStoreBtn>

          <TrialBadge>
            <span>7-Day Free Trial</span>
          </TrialBadge>

          {/* Mobile Menu Button */}
          <MobileMenuButton
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            $isOpen={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </MobileMenuButton>
        </CtaWrap>
      </Nav>

      {/* Mobile Menu Overlay */}
      <MobileOverlay $isOpen={mobileMenuOpen} onClick={closeMobileMenu} />

      {/* Mobile Menu */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <MobileMenuList>
          {NAV_LINKS.map(({ label, href }) => (
            <MobileMenuItem key={href}>
              <MobileMenuLink href={href} onClick={closeMobileMenu}>
                {label}
              </MobileMenuLink>
            </MobileMenuItem>
          ))}
        </MobileMenuList>

        <MobileCtaWrapper>
          <AppStoreBtn
            href="https://apps.apple.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ justifyContent: "center" }}
          >
            <AppleIcon>
              <svg viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 268.9-317.3 70.2 0 128.6 46.4 173 46.4 42.4 0 109.1-49.1 186.3-49.1 30.4 0 110.2 3.9 162.7 69.2zm-85.5-220.5c39.5-46.4 67.8-110.9 67.8-175.4 0-8.9-.6-17.8-2.2-25.4-64.4 2.6-140.5 43.7-185.5 93.8-35.8 39.5-69.7 103.4-69.7 168.5 0 9.5 1.6 19 2.2 22.3 3.9.6 10.4 1.6 16.9 1.6 57.7 0 129.4-39.5 170.5-85.4z" />
              </svg>
            </AppleIcon>
            <AppStoreCopy>
              <AppStoreTop>Download on the</AppStoreTop>
              <AppStoreBottom>App Store</AppStoreBottom>
            </AppStoreCopy>
          </AppStoreBtn>

          <MobileTrialBadge>
            <span>7-Day Free Trial</span>
          </MobileTrialBadge>
        </MobileCtaWrapper>
      </MobileMenu>
    </>
  );
}
